// Imports
// ********************************************
import { useMemo } from 'react';
import { useSwrApi } from '@hooks/useSwrApi';
import { useRouterLocale } from '@hooks/common/useRouterLocale';
import { getI18nLinkHref } from '@services/i18nPaths';

// Wallet menu items land on the Services tab — the wallet section's default tab.
// The backend still sends the legacy transactions link, so we override it here, in the
// single place every consumer (header top actions, sidebar popup, burger menu) reads from.
const WALLET_MENU_IDS = ['cmp-wallet', 'neuro-wallet'];

/**
 * @description Fetch burger menu data. We also check on tab refocus if we need to revalidate the data.
 * We check for SWR data revalidation on tab focus inside the useHeaderInfoData hook, because this
 * component is not mounted normally when that occurs, unless the user leaves the popover opened.
 */
export const useBurgerMenuData = () => {
  // Get locale from router
  const locale = useRouterLocale();


  // SWR: fetch data
  // IMPORTANT: we trigger a re-fetch on tab refocus inside the useHeaderInfoData hook. We do it there
  // because this component is not mounted normally when that occurs, unless the user leaves the popover
  // opened.
  // ********************************************
  const {
    data, error, isLoading, isValidating,
  } = useSwrApi<ApiSchema<'BurgerMenuItem'>[]>({
    url: '/v1/burger-menu',
    locale,
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 36000000, // 10 hours
    },
  });


  // Point wallet items to the Services tab
  // ********************************************
  const burgerMenuData = useMemo(() => data?.map((item) => (
    item.id && WALLET_MENU_IDS.includes(item.id)
      ? { ...item, link: getI18nLinkHref('/wallet/services', locale) }
      : item
  )), [data, locale]);


  // Return data
  // ********************************************
  return {
    burgerMenuData,
    burgerMenuError: error,
    burgerMenuLoading: isLoading,
    burgerMenuValidating: isValidating,
  };
};
